import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../utils/authStore";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "./ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  LightningBoltIcon,
  FileIcon,
  MagicWandIcon,
  LayersIcon,
  ExitIcon,
  PersonIcon,
  GearIcon,
  MagnifyingGlassIcon,
  Share1Icon,
  Pencil2Icon,
  CodeIcon,
  ArchiveIcon,
  SunIcon,
  MoonIcon
} from "@radix-ui/react-icons";
import { ProjectSelector } from "./ProjectSelector";
import { useTheme } from "@/internal-components/ThemeProvider";

interface SidebarItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon, label, isActive }) => {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
        "hover:bg-accent hover:text-accent-foreground",
        isActive && "bg-primary text-primary-foreground shadow-md"
      )}
    >
      {icon}
      <span className="font-medium">{label}</span>
    </Link>
  );
};

export const MainLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const { theme, setTheme } = useTheme();

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const sidebarItems = [
    {
      to: "/projects",
      icon: <ArchiveIcon className="w-5 h-5" />,
      label: "Projects"
    },
    {
      to: "/payloads",
      icon: <CodeIcon className="w-5 h-5" />,
      label: "Payloads"
    },
    {
      to: "/ai-services",
      icon: <MagicWandIcon className="w-5 h-5" />,
      label: "AI Services"
    },
    {
      to: "/instructions",
      icon: <FileIcon className="w-5 h-5" />,
      label: "Instructions"
    },
    {
      to: "/tools",
      icon: <GearIcon className="w-5 h-5" />,
      label: "Tools"
    },
    {
      to: "/folds",
      icon: <LayersIcon className="w-5 h-5" />,
      label: "Folds"
    },
    {
      to: "/vector-loader",
      icon: <MagnifyingGlassIcon className="w-5 h-5" />,
      label: "Vector Loader"
    },
    {
      to: "/flows",
      icon: <LightningBoltIcon className="w-5 h-5" />,
      label: "Flows"
    },
    // {
    //   to: "/share",
    //   icon: <Share1Icon className="w-5 h-5" />,
    //   label: "Share"
    // },
  ];

  const getUserInitials = () => {
    if (!user) return "U";
    const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
    return initials || user.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-2xl font-black text-primary uppercase">
              Frankensten<span className="text-accent">Store</span>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <ProjectSelector />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
            </Button>
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.picture} alt={user.email} />
                      <AvatarFallback className="bg-muted text-muted-foreground">{getUserInitials()}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  {/* <DropdownMenuSeparator /> */}
                  {/* <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <PersonIcon className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem> */}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <ExitIcon className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      {/* Main content area with sidebar */}
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 border-r bg-muted/10 p-4">
          <nav className="space-y-2">
            {sidebarItems.map((item) => (
              <SidebarItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                isActive={location.pathname === item.to}
              />
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};